import {Injectable} from '@angular/core';
import {LangService, LoaderService, NavigateService} from 'ngx-satoris';
import {
  OperationType,
  PassportType,
  Request,
  RequestState,
  RequestSubState,
  RequestTheme,
  RequestWithDuplicata
} from '../models/request';
import {ApiService} from './api.service';
import {Document, DocumentType, UserPermission} from '../models/user';
import {CartService, FormApplication} from './cart.service';
import {FormAction, FormField, VisaFN, VisaFormConfig, VisaFormName} from '../models/forms';
import {Visa} from '../models/VISA-ZIM/local-form';
import {isJsonParsable, parseIndexedData} from '../utils/parseMetadata';
import {parse, isValid, format, differenceInHours} from 'date-fns';
import {environment} from 'src/environments/environment';
import {Person} from '../models/person';
import {Subscription} from 'rxjs';
import {flattenObject} from '../utils/flattenObject';

@Injectable({
  providedIn: 'root'
})
export class RequestService {
  CatA: {value: string, name: string}[] = [];
  CatB: {value: string, name: string}[] = [];
  CatC: {value: string, name: string}[] = [];
  entryPoints: string[] = [];

  allMyPayments: Request[];
  allExtensions: Request[];
  allBatchIds: string[];
  allMyApplications: {batchId: string, requests: Request[] | RequestWithDuplicata[]}[];
  invalidatePersons: Person[] = [];
  private signOutSubscription: Subscription;
  priceOverstay = 10000;

  constructor(private lang: LangService,
    private api: ApiService,
    private loader: LoaderService,
    private nav: NavigateService,
    private cart: CartService) {
    this.signOutSubscription = this.api.signOutObservable.subscribe({
      next: () => {
        this.allMyPayments = undefined;
        this.allExtensions = undefined;
        this.allBatchIds = undefined;
        this.allMyApplications = undefined;
        this.invalidatePersons = undefined;
      }
    });
  }

  ngOnDestroy() {
    this.signOutSubscription.unsubscribe();
  }

  pay(_action?: FormAction, isPayOnline = false) {
    this.loader.loading(true);
    return this.payApplication(_action, isPayOnline).then((res: any) => {
      this.loader.loading(false);
      if(this.api.userRole.isWorker) {
        if(res) {
          this.nav.to('admin-request-select/' + res, undefined, {queryParams: {registerSession: true}});
          setTimeout(() => {
            this.cart.deleteSavedApplication(false);
          });
        } else {
          this.loader.loading(true, {type: 'error', message: 'global.error'});
          return Promise.reject('global.error');
        }
      } else if(res) {
        if(this.api.userRole.isKiosk) {
          this.nav.to('success-kiosk');
        } else {
          this.nav.to('payment-success', undefined, {
            queryParams: {
              paymentId: res
            }
          });
        }
        setTimeout(() => {
          this.cart.deleteSavedApplication(false);
        });
      } else {
        this.loader.loading(true, {type: 'valid', message: 'revision.success'});
        this.nav.to('dashboard');
      }
    }).finally(() => {
      this.invalidatePersons = this.cart.currentApplication.requests.map(request => request.person);
    }).catch((err: any) => {
      this.loader.loading(true, {type: 'error', message: err});
      return Promise.reject(err);
    });
  }

  payApplication(_action?: FormAction, _isPayOnline = false): Promise<any> {
    return new Promise((_resolve, _reject) => {
      const payments: { request_id: string, person_id: number, serialized: string }[] = [];
      this.cart.currentApplication.requests.forEach((request: Request) => {
        const data = {
          AddressZimbabwe: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.ADDRESS_ZIMBABWE, request.formConfig) || undefined,
          ArrivalDate: this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.ARRIVAL_DATE, request.formConfig) ? new Date(this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.ARRIVAL_DATE, request.formConfig)).getTime() : undefined,
          BirthDay: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.BIRTH_DAY, request.formConfig) ? new Date(this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.BIRTH_DAY, request.formConfig)).getTime() : undefined,
          DateOfExpiry: this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.DATE_OF_EXPIRY, request.formConfig) ? new Date(this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.DATE_OF_EXPIRY, request.formConfig)).getTime() : undefined,
          DateOfIssue: this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.DATE_OF_ISSUE, request.formConfig) ? new Date(this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.DATE_OF_ISSUE, request.formConfig)).getTime() : undefined,
          DepartureDate: this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.DEPARTURE_DATE, request.formConfig) ? new Date(this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.DEPARTURE_DATE, request.formConfig)).getTime() : undefined,
          Documents: {
            PurposeOfVisit: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.PURPOSE_OF_VISIT, request.formConfig) || undefined,
            PassportPhoto: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.PASSPORT_PHOTO, request.formConfig) || undefined,
            PassportScan: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.PASSPORT_SCAN, request.formConfig) || undefined,
            ProofOfResidenceHost: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.PROOF_OF_RESIDENCE_HOST, request.formConfig) || undefined,
            AddressAtDestination: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.ADDRESS_AT_DESTINATION, request.formConfig) || undefined,
            PreviousZimbabweanVisa: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.PREVIOUS_ZIMBABWEAN_VISA, request.formConfig) || undefined,
            HostInvitationLetter: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.HOST_INVITATION_LETTER, request.formConfig) || undefined,
            ProofOfResidence: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.PROOF_OF_RESIDENCE, request.formConfig) || undefined,
            ApplicationLetter: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.APPLICATION_LETTER, request.formConfig) || undefined,
            ResidentialStatusOfHost: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.RESIDENTIAL_STATUS_OF_HOST, request.formConfig) || undefined,
            ReasonOfVisitProof: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.REASON_OF_VISIT_PROOF, request.formConfig) || undefined,
            BusinessLetter: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.BUSINESS_LETTER, request.formConfig) || undefined,
            BusinessProfile: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.BUSINESS_PROFILE, request.formConfig) || undefined,
            InvitationLetterAndBusinessProfile: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.INVITATION_LETTER_AND_BUSINESS_PROFILE, request.formConfig) || undefined,
            LetterOfAcceptanceFromSchool: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.LETTER_OF_ACCEPTANCE_FROM_SCHOOL, request.formConfig) || undefined,
            InvitationLetter: this.getFieldValue(VisaFormName.DOCUMENT, VisaFN.INVITATION_LETTER, request.formConfig) || undefined
          },
          Email: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.EMAIL, request.formConfig) || undefined,
          FirstName: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.FIRST_NAME, request.formConfig) || undefined,
          Gender: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.GENDER, request.formConfig) || undefined,
          HomeAddress: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.HOME_ADDRESS, request.formConfig) || undefined,
          HostName: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.HOST_NAME, request.formConfig) || undefined,
          LastName: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.LAST_NAME, request.formConfig) || undefined,
          MaritalStatus: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.MARITAL_STATUS, request.formConfig) || undefined,
          Nationality: request.nationality || undefined,
          Occupation: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.OCCUPATION, request.formConfig) || undefined,
          PassportNumber: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.PASSPORT_NUMBER, request.formConfig) || undefined,
          Phone: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.PHONE, request.formConfig) || undefined,
          PlaceOfBirth: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.PLACE_OF_BIRTH, request.formConfig) || undefined,
          PlaceofIssue: this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.PLACE_OF_ISSUE, request.formConfig) || undefined,
          PreviousConvictionsZim: this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_ZIM, request.formConfig) || undefined,
          PreviousConvictionsHome: this.getFieldValue(VisaFormName.TRAVEL_INFORMATION, VisaFN.PREVIOUS_CONVICTIONS_HOME, request.formConfig) || undefined,
          Spouse: this.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE, request.formConfig) ? this.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE, request.formConfig).value ? 'Yes' : 'No' : undefined,
          SpouseBirthDay: this.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE, request.formConfig) ? this.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE, request.formConfig).value ? new Date(this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE_DATE_OF_BIRTH, request.formConfig)).getTime() : undefined : undefined,
          SpouseFullName: this.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE, request.formConfig) ? this.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE, request.formConfig).value ? this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE_NAME, request.formConfig) : undefined : undefined,
          SpousePassportNumber: this.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE, request.formConfig) ? this.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE, request.formConfig).value ? this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE_PASSPORT_NUMBER, request.formConfig) : undefined : undefined,
          SpousePlaceOfBirth: this.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE, request.formConfig) ? this.getField(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE, request.formConfig).value ? this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.SPOUSE_PLACE_OF_BIRTH, request.formConfig) : undefined : undefined,
          NextOfKin: JSON.stringify({
            firstname: this.getFieldValue(VisaFormName.EMERGENCY_CONTACT, VisaFN.FIRST_NAME, request.formConfig) || undefined,
            lastname: this.getFieldValue(VisaFormName.EMERGENCY_CONTACT, VisaFN.LAST_NAME, request.formConfig) || undefined,
            phone: this.getFieldValue(VisaFormName.EMERGENCY_CONTACT, VisaFN.PHONE, request.formConfig) || undefined,
            email: this.getFieldValue(VisaFormName.EMERGENCY_CONTACT, VisaFN.EMAIL, request.formConfig) || undefined
          }),
          FundsAvailable: Number(this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE, request.formConfig) || 0),
          FundsAvailableCurrency: this.getFieldValue(VisaFormName.PERSONAL_DETAILS, VisaFN.FUNDS_AVAILABLE_CURRENCY, request.formConfig) || undefined,
          Urgent: this.isPaidOnline(request.visaType.id) ? (request.urgent === undefined ? 'No' : request.urgent ? 'Yes' : 'No') : undefined,
          PassportType: request.passportType || undefined
        } as Visa;

        Object.keys(data).forEach((key: keyof Visa) => {
          if(data[key] === undefined) {
            delete data[key];
          }
        });
        payments.push({
          request_id: request.visaType.id,
          person_id: request.person.id ? Number(request.person.id) : undefined,
          serialized: JSON.stringify(data)
        });
      });

      /*let previousBatchId = undefined;
      if(this.api.userRole.isCustomer && !this.cart.currentApplication?.isExtend) {
        previousBatchId = this.cart.currentApplication.isRevision ? this.cart.currentApplication.travelName : (this.allBatchIds?.find(b => b === this.cart.currentApplication.travelName) || undefined);
      }*/
      if(this.api.userRole.isWorker && this.api.hasPerm(UserPermission.ALLOW_CREATE) && this.api.hasPerm(UserPermission.ALLOW_CONSUME)) {
        /*this.api.registerMNTAPayment(payments, this.cart.currentApplication.travelName, previousBatchId, false).then(res => {
          this.cart.currentApplication.submitted = true;
          if(res.link) {
            this.cart.saveCurrentApplication(false);
            window.open(res.link, '_self');
          } else {
            this.invalidatePersons = this.cart.currentApplication.requests.map(request => request.person);
            resolve(res.ids[0]);
          }
          this.loader.loading(false);
        }).catch(err => {
          reject(err);
        });*/
      } else if(this.cart.currentApplication.isRevision) {
        /*this.api.updatePayment(this.cart.currentApplication.requestId,
          undefined,
          undefined,
          payments[0].serialized,
          undefined,
          undefined).then(() => {
          this.invalidatePersons = this.cart.currentApplication.requests.map(request => request.person);
          resolve(undefined);
        }).catch((err) => {
          reject(err);
        });*/
      } else {
        /*this.api.registerMNTAPayment(payments, this.cart.currentApplication.travelName, previousBatchId, isPayOnline).then(res => {
          if(this.api.userRole.isCustomer) {
            this.allBatchIds.push(this.cart.currentApplication.travelName);
          }
          this.cart.currentApplication.submitted = true;
          if(res.link) {
            this.cart.saveCurrentApplication(false);
            window.open(res.link, '_self');
          } else {
            this.invalidatePersons = this.cart.currentApplication.requests.map(request => request.person);
            resolve(res.ids[0]);
          }
          this.loader.loading(false);
        }).catch(err => {
          reject(err);
        });*/
      }
    });
  }


  getStatusTheme(request: RequestWithDuplicata, onlyState = false): RequestTheme {
    const isUsage = this.isUsage(request);
    const isBlacklist = this.isBlacklisted(request);
    const document = this.api.userInfo.server.paymentRequests.find(d => d.id === request.operationId);
    const indexedDatas = this.api.env.type === DocumentType.PASS ? '' : parseIndexedData(request.internalIndexedData, request.operationId, this.api.userInfo.server);
    const isUrgent =  this.api.env.type === DocumentType.ZWEVISA  && indexedDatas &&  indexedDatas?.urgent === 'Yes';
    switch(request.state) {
    case RequestState.PROPOSED:
      return {
        severity: 3,
        color: '#C9C9C9',
        iconColor: '#747474',
        icon: 'clock',
        state: this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.PROPOSED),
        descState: this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.PROPOSED)
      };
    case RequestState.DUPLICATED:
      return {
        severity: 2,
        color: '#FFA14A',
        iconColor: '#A85100',
        icon: 'copy',
        state: this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.DUPLICATED),
        descState: this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.DUPLICATED)
      };
    case RequestState.REFUNDED:
      return {
        severity: 2,
        color: '#FF823C',
        iconColor: '#9D3900',
        icon: 'undo',
        state: this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.REFUNDED),
        descState: this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.REFUNDED)
      };
    case RequestState.DELAY_PAY:
      return {
        severity: 2,
        color: isUrgent ? '#FF4136' : '#C9F88D',
        iconColor: isUrgent ? '#A11A12' : '#6ABD00',
        icon: (request.operationId === DocumentType.ZWEVISAEXTAB || request.operationId === DocumentType.ZWEVISAEXTC) ? 'circle-check' : 'circle-check',
        state: (request.operationId === DocumentType.ZWEVISAEXTAB || request.operationId === DocumentType.ZWEVISAEXTC) ? this.lang.transform('zwevisaExtValid') : onlyState ? 'readyCheck' : this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.DELAY_PAY + (request.operationId === DocumentType.ZWEENTRY ? '.entry' : request.operationId === DocumentType.ZWEEXIT ? '.exit' : '')),
        descState: (request.operationId === DocumentType.ZWEVISAEXTAB || request.operationId === DocumentType.ZWEVISAEXTC) ? this.lang.transform('zwevisaExtValid.desc') : this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.DELAY_PAY + (request.operationId === DocumentType.ZWEENTRY ? '.entry' : request.operationId === DocumentType.ZWEEXIT ? '.exit' : ''))
      };
    case RequestState.EXPIRED:
      return {
        severity: 3,
        color: '#F88D8D',
        iconColor: '#B50000',
        icon: isBlacklist ? 'ban' : 'xmark-circle',
        state: onlyState ? 'refused' : this.lang.transform(((request.operationId === DocumentType.ZWEVISAEXTAB || request.operationId === DocumentType.ZWEVISAEXTC) ? 'payment_status.extension.DYN.' :'payment_status.DYN.') + this.api.env.type + '.' + RequestState.EXPIRED + (isBlacklist ? '.blacklisted' : '')),
        descState: this.lang.transform(((request.operationId === DocumentType.ZWEVISAEXTAB || request.operationId === DocumentType.ZWEVISAEXTC) ? 'payment_status.desc.extension.DYN.' :'payment_status.desc.DYN.') + this.api.env.type + '.' + RequestState.EXPIRED + (isBlacklist ? '.blacklisted' : ''))
      };
    case RequestState.PENDING:
      return {
        severity: 4,
        color: '#FFE455',
        iconColor: '#B39600',
        icon: 'clock',
        state: this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.PENDING),
        descState: this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.PENDING)
      };
    case RequestState.PAID:
      if(this.api.env.type === DocumentType.PASS) {
        return {
          severity: 2,
          color: '#8FF88D',
          iconColor: '#03B000',
          icon: 'check',
          state: this.lang.transform('payment_status.DYN.' + this.api.env.type  + '.' + RequestState.PAID),
          descState: this.lang.transform('payment_status.desc.DYN.' + this.api.env.type  + '.' + RequestState.PAID)
        };
      }
    case RequestState.USED:
    case RequestState.PAID:
      if(request.subState === RequestSubState.REFUSED_IN || request.subState === RequestSubState.REFUSED_OUT) {
        return {
          severity: 3,
          color: '#F88D8D',
          iconColor: '#B50000',
          icon: 'xmark-circle',
          state: this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.EXPIRED + (request.subState === RequestSubState.REFUSED_IN ? '.checkIn' : '.checkOut')),
          descState: this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.EXPIRED + (request.subState === RequestSubState.REFUSED_IN ? '.checkIn' : '.checkOut'))
        };
      }
      if(request.subState === RequestSubState.CLOSED) {
        return {
          severity: 1,
          color: '#B2C3FF',
          iconColor: '#4A66CC',
          icon: 'right-from-bracket',
          state: onlyState ? 'closed' : this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.USED + '.' + 'closed'),
          descState: this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.USED + '.' + 'closed')
        };
      }
      if(request.subState === RequestSubState.CHECKED_IN && document.multiUsage) {
        return {
          severity: 2,
          color: isUrgent ? '#FF4136' : '#8DE5F8',
          iconColor: isUrgent ? '#A11A12' : '#0094B4',
          icon: 'right-from-bracket',
          state: onlyState ? 'readyCheckOut' : this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.USED + '.' + 'out'),
          descState: this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.USED + '.' + 'out')
        };
      } else {
        if(isUsage && request?.usageCount < document.usageCount) {
          return {
            severity: 2,
            color: isUrgent ? '#FF4136' : '#8FF88D',
            iconColor: isUrgent ? '#A11A12' : '#03B000',
            icon: 'right-from-bracket',
            state: onlyState ? (request.operationId === DocumentType.ZWEEXIT ? 'readyZweexit' : 'readyCheckIn') : (request.operationId === DocumentType.ZWEVISAEXTAB || request.operationId === DocumentType.ZWEVISAEXTC) ? this.lang.transform('zwevisaExtValid') : this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.USED + (this.api.env.type === DocumentType.PASS ? '' :  ('.' + (request.operationId === DocumentType.ZWEEXIT ? 'out' : 'in')))),
            descState: (request.operationId === DocumentType.ZWEVISAEXTAB || request.operationId === DocumentType.ZWEVISAEXTC) ? this.lang.transform('zwevisaExtValid.desc') : this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.USED + (this.api.env.type === DocumentType.PASS ? ''  : ('.' + (request.operationId === DocumentType.ZWEEXIT ? 'zweexit' : 'in'))))
          };
        } else {
          return {
            severity: 1,
            color: '#B2C3FF',
            iconColor: '#4A66CC',
            icon: 'right-from-bracket',
            state: onlyState ? (request.operationId === DocumentType.ZWEENTRY ? 'entryFinish' : 'outFinish') :  this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.USED + (this.api.env.type === DocumentType.PASS ? '' : ('.' + (request.operationId === DocumentType.ZWEENTRY ? 'entryFinish' : 'outFinish')))),
            descState: this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.USED + (this.api.env.type === DocumentType.PASS ? '' : ('.' + (request.operationId === DocumentType.ZWEENTRY ? 'entryFinish' : 'outFinish'))))
          };
        }
      }
    case RequestState.PAID_NOT_CONFIRMED:
    case RequestState.DELAY_PAY_NOT_CONFIRMED:
      if(this.api.env.type === DocumentType.PASS) {
        return {
          severity: 3,
          color: '#FF823C',
          iconColor: '#9D3900',
          icon: 'clock',
          state: this.lang.transform('payment_status.DYN.' + this.api.env.type  + '.' + RequestState.PAID_NOT_CONFIRMED),
          descState: this.lang.transform('payment_status.desc.DYN.' + this.api.env.type  + '.' + RequestState.PAID_NOT_CONFIRMED)
        };
      }
      const isRevisionSinceOneWeek = new Date().getTime() - new Date(request.createdAt).getTime() > 604800000;
      if((request.subState === RequestSubState.REVISIONS_0 && request.operationId !== DocumentType.ZWEVISAEXTAB) || request.subState === RequestSubState.REVISIONS_1) {
        return {
          severity: 3,
          color: (isRevisionSinceOneWeek || isUrgent) ? '#FF4136' : '#FFA14A',
          iconColor: (isRevisionSinceOneWeek || isUrgent) ? '#A11A12' : '#A85100',
          icon: 'clock',
          state: request.operationId === DocumentType.ZWEVISAEXTC ?  request.subState === RequestSubState.REVISIONS_1 ? this.lang.transform('zwevisaExtC.finalReview.state') : this.lang.transform('zwevisaExtC.review.state') : this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.PAID_NOT_CONFIRMED + (this.api.userRole.isCustomer ? '' : ('.' + 'review')), {reviewCount: request.subState}),
          descState: request.operationId === DocumentType.ZWEVISAEXTC ? request.subState === RequestSubState.REVISIONS_1 ? this.lang.transform('zwevisaExtC.finalReview.desc') :  this.lang.transform('zwevisaExtC.review.desc') : this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.PAID_NOT_CONFIRMED + (this.api.userRole.isCustomer ? '' : ('.' + 'review')), {reviewCount: request.subState}) + (isRevisionSinceOneWeek ? ' ' + this.lang.transform('sinceOneWeek') : '')
        };
      } else if(request.subState === RequestSubState.REVISIONS_2 || request.operationId === DocumentType.ZWEVISAEXTAB) {
        return {
          severity: 3,
          color: (isRevisionSinceOneWeek || isUrgent) ? '#FF4136' : '#FF823C',
          iconColor: (isRevisionSinceOneWeek || isUrgent) ? '#A11A12' : '#9D3900',
          icon: 'clock',
          state: this.lang.transform('payment_status.DYN.' + this.api.env.type + '.' + RequestState.PAID_NOT_CONFIRMED + (this.api.env.type === DocumentType.ZWEVISA ? (this.api.userRole.isCustomer ? '' : '.finalReview') : '')),
          descState: request.operationId === DocumentType.ZWEVISAEXTAB ? this.lang.transform('final.ZWEVISAEXTAB') : this.lang.transform('payment_status.desc.DYN.' + this.api.env.type + '.' + RequestState.PAID_NOT_CONFIRMED + (this.api.env.type === DocumentType.ZWEVISA ? (this.api.userRole.isCustomer ? '' : '.finalReview') : '')) + (isRevisionSinceOneWeek ? ' ' + this.lang.transform('sinceOneWeek') : '')
        };
      }
    default:
      return {
        severity: 2,
        color: '#C9C9C9',
        iconColor: '#747474',
        icon: 'question',
        state: request.state + '.state',
        descState: request.state + '.desc'
      };
    }
  }

  getHighestSeverityTheme = (app: {batchId: string; requests: Request[]}, request?: Request | RequestWithDuplicata): RequestTheme => {
    let highestSeverity: RequestTheme;
    if(request) {
      const requestTheme = this.getStatusTheme(request as RequestWithDuplicata);
      const extensionsThemes = request.extensions?.map(ext => this.getStatusTheme(ext as RequestWithDuplicata))[0];
      highestSeverity = extensionsThemes && extensionsThemes.severity > requestTheme.severity ? extensionsThemes : requestTheme;

    } else {
      const requestThemes = app.requests.map(req => this.getStatusTheme(req as RequestWithDuplicata));
      const requestsHighestSeverity = requestThemes.length && requestThemes.reduce((prev, curr) => prev.severity > curr.severity ? prev : curr);
      const extensionsThemes = app.requests.map(req => req.extensions?.map(ext => this.getStatusTheme(ext as RequestWithDuplicata))[0])?.filter(ext => ext !== undefined);
      const extensionsHighestSeverity = extensionsThemes.length && extensionsThemes.reduce((prev, curr) => prev.severity > curr.severity ? prev : curr);
      highestSeverity = extensionsHighestSeverity && extensionsHighestSeverity.severity > requestsHighestSeverity.severity ? extensionsHighestSeverity : requestsHighestSeverity;
    }
    return highestSeverity;
  };

  getLatestModificationDate(app: { batchId: string; requests: Request[] }): string {
    return app.requests.reduce((prev, curr) => prev.updatedOn > curr.updatedOn ? prev : curr).updatedOn;
  }

  getBatch(batchId: string) {
    return this.allMyApplications.find(app => app.batchId === batchId);
  }

  isDocumentType(document: DocumentType): boolean {
    const documentType = this.api.userInfo.server.paymentRequests.find(d => d.id === document);
    return documentType.customer;
  }

  isPaidOnline(document: DocumentType | string): boolean {
    const documentType = this.api.userInfo.server.paymentRequests.find(d => d.id === document);
    return documentType?.paidOnline;
  }

  isNextCheckout(request: RequestWithDuplicata): boolean {
    return request?.events?.filter(e => (e.internalIndexedData || '').indexOf(`|${OperationType.IN}`) > -1).length > request?.events?.filter(e => (e.internalIndexedData || '').indexOf(`|${OperationType.OUT}`) > -1).length;
  }

  detectCheckRefusal(request: RequestWithDuplicata): boolean | OperationType {
    if(request?.events?.length > 0) {
      const lastEvent = request.events[request.events.length - 1];
      const firstEvent = request.events[0];
      if((firstEvent.internalIndexedData || '').includes(`|${OperationType.REFUSEIN}`) || (lastEvent.internalIndexedData || '').includes(`|${OperationType.REFUSEIN}`)) {
        return OperationType.REFUSEIN;
      } else if((firstEvent.internalIndexedData || '').includes(`|${OperationType.REFUSEOUT}`) || (lastEvent.internalIndexedData || '').includes(`|${OperationType.REFUSEOUT}`)) {
        return OperationType.REFUSEOUT;
      }
    }
    return false;
  }

  isUsage(request: RequestWithDuplicata): boolean {
    const document = this.api.userInfo.server.paymentRequests.find(d => d.id === request.operationId);
    if(document?.isUsage) {
      return eval(document.isUsage)({Operation: this.isNextCheckout(request) ? OperationType.OUT : OperationType.IN});
    } else {
      return false;
    }
  }

  getFieldValue(formName: VisaFormName, fieldName: VisaFN, config: VisaFormConfig): any {
    const c = config;
    if(!c[formName]?.fields) return undefined;
    return (typeof c[formName].fields?.find((field: FormField) => field.name === fieldName)?.value === 'string' ? c[formName].fields?.find((field: FormField) => field.name === fieldName)?.value : c[formName].fields?.find((field: FormField) => field.name === fieldName)?.value) || undefined;
  }

  getField(formName: VisaFormName, fieldName: string, config: VisaFormConfig): FormField {
    const c = config;
    return c[formName]?.fields?.find((field: FormField) => field.name === fieldName);
  }

  setCountries(): Promise<boolean> {
    if(this.api.isZwevisa) {
      return this.api.schema('zweentry').then((res: any) => {
        const nationalities = res?.properties?.Nationality;
        this.CatA = nationalities?.enumA.map((n: string) => ({value: n, name: this.lang.transform('DYN.' + n)}));
        this.CatB = nationalities?.enumB.map((n: string) => ({value: n, name: this.lang.transform('DYN.' + n)}));
        this.CatC = nationalities?.enumC.map((n: string) => ({value: n, name: this.lang.transform('DYN.' + n)}));
        return true;
      });
    }
    return Promise.resolve(true);
  }

  getNationalities(withRefugees: boolean): { value: string, name: string }[] {
    const noDeclarations: {value: string, name: string}[] = [];
    const sorted = [...this.CatA, ...this.CatB, ...this.CatC].filter(n => withRefugees? true : !n.value.startsWith('XX')).sort((a, b) => {
      if(environment.nationalityNoDeclaration.includes(a.value)) noDeclarations.push(a);
      if(environment.nationalityNoDeclaration.includes(b.value)) noDeclarations.push(b);
      return a.name.localeCompare(b.name);
    });
    if(noDeclarations.length) {
      noDeclarations.sort((a, b) => a.name.localeCompare(b.name));
      noDeclarations.forEach((nd) => {
        const index = sorted.findIndex((n) => n.value === nd.value);
        if(index > -1) {
          sorted.splice(index, 1);
          sorted.unshift(nd);
        }
      });
    }
    return sorted;
  }

  isCountryCat(country: string, cat: 'A' | 'B' | 'C'): boolean {
    const categoryKey: keyof RequestService = `Cat${cat}` as keyof RequestService;
    const category = this[categoryKey];
    if(Array.isArray(category)) {
      return category.findIndex((c: any) => typeof c !== 'string' ? c.value === country : c === country) > -1;
    }
    return false;
  }


  convertCurrency(sourceCurrency: string, amount: number, targetCurrency: string) {
    const sourceRate = this.api.userInfo.rates.rates[sourceCurrency];
    const targetRate = this.api.userInfo.rates.rates[targetCurrency];
    if(!sourceRate || !targetRate) {
      return null;
    }
    const convertedAmount = (amount / sourceRate * targetRate).toFixed(2);
    return {
      currency: targetCurrency,
      amount: convertedAmount
    };
  }

  isUsageDateValid(date: string, type: 'usageAfter' | 'usageUntil') {
    if(type === 'usageAfter') {
      return new Date() >= new Date(date);
    } else {
      return new Date() <= new Date(date);
    }
  }


  isLocalUsageDateValid(date: any) {
    let parsedDate;
    if(typeof date === 'string') {
      parsedDate = parse(date, 'dd/MM/yyyy HH:mm', new Date());
    } else if(date instanceof Date && isValid(date)) {
      parsedDate = date;
    } else {
      return false;
    }
    return new Date() < parsedDate;
  }

  isBlacklisted(request: RequestWithDuplicata): boolean {
    return ['blacklist', 'black list', 'black-list', 'blacklisted', 'black-listed'].some(blacklist => request?.refusalReason?.toLowerCase().includes(blacklist));
  }

  cleanupPaymentUrl(url: string, storeReason = false) {
    if(this.checkReasonInUrl(url)) {
      const urlObj = new URL(url);
      const reason = urlObj.searchParams.get('reason');
      urlObj.searchParams.delete('reason');
      const newUrl = urlObj.toString();
      if(storeReason) {
        sessionStorage.setItem('paymentReason', reason);
      }
      window.location.replace(newUrl);
    }
  }

  checkReasonInUrl(url: string) {
    const urlObj = new URL(url);
    return urlObj.searchParams.has('reason');
  }

  isExtension(request: Request): boolean {
    return request && (request.operationId === DocumentType.ZWEVISAEXTAB || request.operationId === DocumentType.ZWEVISAEXTC);
  }

  checkIfPendingWaiting() {
    if(this.api.userRole.isCustomer) {
      const openDialog = (req: Request) => {
        this.loader.loading(true, {type: 'warn', message: 'ask.pendingRequest', btnLabel: 'yes', custom: {icon: 'clock', closeBtnLabel: 'no'}}).then((done) => {
          if(done) this.nav.to('request-account/' + req.id);
        });
      };
      this.allMyApplications?.forEach((app) => {
        app.requests.forEach((req) => {
          if(req.state === RequestState.PENDING) {
            openDialog(req);
            return;
          }
          if(req.extensions.length) {
            req.extensions.forEach((ext) => {
              if(ext.state === RequestState.PENDING) {
                openDialog(req);
                return;
              }
            });
          }
        });
      });
    }
  }

  calculateLocalUntil(documentId: DocumentType, passportType: PassportType | string, nationality: string) {
    if(passportType === undefined) {
      return undefined;
    }
    try {
      const localUntilFromServerString = this.api.userInfo.server.paymentRequests.find(doc => doc.id === documentId)?.localUsageUntilFromNowMillis;
      if(localUntilFromServerString === undefined) {
        return undefined;
      }
      const localUntilFromServer = eval(localUntilFromServerString);
      const unserialized = {PassportType: passportType, Nationality: nationality};
      return localUntilFromServer(unserialized);
    } catch (e) {
      return undefined;
    }
  }

  localUntilText(payment: RequestWithDuplicata) {
    const indexedDatas = parseIndexedData(payment.internalIndexedData, payment.operationId, this.api.userInfo.server);
    const localUntilFromServer = this.calculateLocalUntil(payment.operationId, indexedDatas.passportType, indexedDatas.nationality);
    let isNoDeclaration = false;
    if(localUntilFromServer === undefined) {
      return '';
    }
    if(localUntilFromServer === 0) {
      isNoDeclaration = true;
    }
    if(payment.subState === RequestSubState.REFUSED_IN || payment.operationId === DocumentType.ZWEEXIT  || this.getStatusTheme(payment, true).state === 'outFinish' || payment.operationId === DocumentType.ZWEVISAEXTC || payment.operationId === DocumentType.ZWEVISAEXTAB) {
      return this.lang.transform('none');
    } else if(isNoDeclaration) {
      return this.lang.transform('no.limit');
    } else if(this.getStatusTheme(payment, true).state === 'readyCheckIn' || this.getStatusTheme(payment, true).state === 'readyCheck' || payment.state ===  RequestState.PAID_NOT_CONFIRMED || payment.state === RequestState.EXPIRED || payment.state === RequestState.PENDING) {
      return this.lang.transform('to.determined');
    } else {
      if(!payment?.localUsageUntil) {
        return '';
      }
      return format(payment?.localUsageUntil, 'dd/MM/yyyy HH:mm');
    }
  }

  getRequestData(specificPerson?: Person[]): Promise<void> {
    const relevantPersons = specificPerson?.length ? specificPerson : this.api.listPersons;
    return Promise.all(relevantPersons.map((person: Person) => this.api.personRequests(person.id))).then((payments: Request[][]) => {
      const paymentData = payments.reduce((acc, curVal) => acc.concat(curVal), []);
      paymentData.forEach((payment) => {
        if(payment.internalIndexedData) {
          payment.metadata = parseIndexedData(payment.internalIndexedData, payment.operationId, this.api.userInfo.server);
        } else {
          payment.metadata = isJsonParsable(payment?.metadata);
        }
      });
      if(this.allMyPayments?.length) {
        paymentData.forEach((newPayment) => {
          const existingPaymentIndex = this.allMyPayments.findIndex(payment => payment.id === newPayment.id);
          if(existingPaymentIndex >= 0) {
            this.allMyPayments[existingPaymentIndex] = newPayment;
          } else {
            this.allMyPayments.push(newPayment);
          }
        });
      } else {
        this.allMyPayments = paymentData;
      }
      this.allMyPayments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      // get all batchIds for each person
      this.api.listPersons.forEach((person: Person) => {
        person.batchIds = person.batchIds || [];
        this.allMyPayments.forEach((payment: Request) => {
          if(payment.person_id === person.id && payment.batchId !== null) {
            person.batchIds.push(payment.batchId);
          }
        });
      });
      this.allMyApplications = undefined;
      // get all batchIds for all users
      this.allBatchIds = (specificPerson?.length ? this.allMyPayments : paymentData)?.reduce((acc: string[], request: Request) => {
        if(!acc.includes(request.batchId) && request.batchId && request.operationId !== DocumentType.ZWEVISAEXTAB && request.operationId !== DocumentType.ZWEVISAEXTC) {
          acc.push(request.batchId);
        }
        return acc;
      }, []) || [];
      // get all extensions for all users
      this.allExtensions = paymentData?.filter((request: Request) => request.operationId === DocumentType.ZWEVISAEXTAB || request.operationId === DocumentType.ZWEVISAEXTC) || [];
      //get all applications for all users sorted by batchIds, with each their assigned requests and their extensions within those requests
      this.allMyApplications = this.allBatchIds.map((batchId: string) => ({
        batchId,
        requests: this.allMyPayments.filter((request: Request) => request.batchId === batchId).map((request: Request) => {
          request.extensions = this.allExtensions.filter((extension: Request) => {
            const extensionIndexedData = parseIndexedData(extension.internalIndexedData, extension.operationId, this.api.userInfo.server);
            return extensionIndexedData.visaReference === request.id;
          });
          return request;
        })
      }));
      this.invalidatePersons = [];
    });
  }

  getNameRequest(request: Request): string {
    const {operationId, id, metadata} = request;
    if(operationId === DocumentType.ZWEVISAEXTAB || operationId === DocumentType.ZWEVISAEXTC) {
      const linkedRequest = this.allMyPayments.find(payment => {
        if(payment.extensions && payment.extensions.length) {
          return payment.extensions.find(extension => extension.id === id);
        } else {
          return false;
        }
      });
      if(linkedRequest) {
        const {firstName, lastName} = linkedRequest.metadata;
        return `${firstName} ${lastName}`;
      }
    } else if(metadata?.firstName && metadata?.lastName) {
      return `${metadata.firstName} ${metadata.lastName}`;
    }
    return '';
  }

  isSubjectToUntil(request: Request): boolean {
    const dataActualDocument = this.api.userInfo.server.paymentRequests.find(document => document.id === request?.operationId);
    if(request.operationId === DocumentType.ZWEVISAEXTC || request.operationId === DocumentType.ZWEVISAEXTAB) return false;
    return eval(dataActualDocument.usageUntilFromNowMillis)({
      Nationality: request.serialized.Nationality,
      PassportType: request.serialized.PassportType
    });
  }

  hasNoAwaitingExtension(request: Request): boolean {
    if(!request?.extensions) return true;
    return request?.extensions?.every((request: Request) => request.state !== RequestState.PENDING && request.state !== RequestState.PAID_NOT_CONFIRMED && request.state !== RequestState.DELAY_PAY_NOT_CONFIRMED);
  }

  startExtend(request: Request, metadata?: any) {
    this.loader.loading(true);
    const extendVisaFormConfig: VisaFormConfig = JSON.parse(JSON.stringify(this.cart.getOriginalForm(DocumentType.ZWEVISAEXTC)));

    const meta = metadata || flattenObject(request.serialized as Visa);
    const extendApplication: FormApplication = {
      travelName: undefined,
      plannedEntry: meta.ProvisionnalEntryPort,
      requests: [{
        formConfig: extendVisaFormConfig,
        nationality: meta.Nationality,
        passportType: meta.PassportType,
        visaType: this.api.userInfo.server.paymentRequests.find((doc: Document) => this.isCountryCat(meta.Nationality, 'C') ? doc.id === DocumentType.ZWEVISAEXTC : doc.id === DocumentType.ZWEVISAEXTAB),
        urgent: meta.Urgent === 'Yes',
        person: {
          firstName: meta.FirstName || this.api.listPersons.find((person: Person) => person.id === request.person_id)?.firstName || '',
          lastName: meta.LastName || this.api.listPersons.find((person: Person) => person.id === request.person_id)?.lastName || '',
          nationalNumber: '',
          batchIds: [],
          passportNumber: '',
          id: metadata ? undefined : Number(request.person_id),
          linkType: null,
          otherLinkType: ''
        }
      }],
      visaFeesAndTerms: false,
      isExtend: true,
      requestId: request.id
    };

    this.loader.loading(true, {type: 'info', message: 'extend.start', btnLabel: 'yes', custom: {closeBtnLabel: 'no'}}).then((done ) => {
      if(done) {
        this.cart.startApplication(false, extendApplication, extendVisaFormConfig);
      }
    });
  }

  getBatchIds(persons: Person[]) {
    persons.forEach((person: Person) => {
      person.batchIds = person.batchIds || [];
      this.allMyPayments.forEach((payment: Request) => {
        if(payment.person_id === person.id && payment.batchId !== null) {
          person.batchIds.push(payment.batchId);
        }
      });
    });
  }

  isUnderReview(request: RequestWithDuplicata): boolean {
    return (request.state === RequestState.PAID_NOT_CONFIRMED || request.state === RequestState.DELAY_PAY_NOT_CONFIRMED) &&
      (request.subState === RequestSubState.REVISIONS_0 ||
        request.subState === RequestSubState.REVISIONS_1 ||
        request.subState === RequestSubState.REVISIONS_2 ||
        request.subState === RequestSubState.REVISIONS_3_MORE);
  }

  getPersonsWithReviewStatus(persons: Person[]) {
    if(!persons.length) return [];
    if(!persons.some(person => person.batchIds)) {
      this.getBatchIds(persons);
    }
    return persons.map((person) => {
      const shouldDisable = (predicate: (request: RequestWithDuplicata) => boolean) => person?.batchIds?.some((batchId) => {
        const apps = this.allMyApplications.filter(app => app.batchId === batchId);
        return apps && apps.some((app) => app.requests && app.requests.some((request: RequestWithDuplicata) => request.person_id === person.id && predicate(request)));
      }) || false;
      const isOlderThanOneDay = (request: RequestWithDuplicata) => differenceInHours(new Date(), new Date(request.createdAt)) >= 24;
      return {
        ...person,
        disabledBecauseReview: shouldDisable((request) => this.isUnderReview(request)),
        disabledBecauseDraft: shouldDisable((request) => request.state === RequestState.PROPOSED && !isOlderThanOneDay(request)),
        disabledBecauseDraftWarn: shouldDisable((request) => request.state === RequestState.PROPOSED && isOlderThanOneDay(request))
      };
    });
  }
}
